import {
  decodeImage,
  EventType,
  Fit,
  Layout,
  Rive,
  type ViewModelInstanceTrigger,
} from '@rive-app/canvas-lite'
import { useQueryClient } from '@tanstack/solid-query'
import {
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  Show,
} from 'solid-js'
import stampSvg from '~/assets/figma/stamp.svg'
import rewardRiv from '~/assets/rive/reward.riv'
import { useLocalizedTreeName } from '~/hooks/use-localized-tree-name'
import { animateCount } from '~/lib/animate-count'
import { classifyRiveStates } from '~/lib/classify-rive-state'
import {
  deriveRewardPresentation,
  type RewardPresentation,
} from '~/lib/derive-reward-presentation'
import {
  flyingStampsFrom,
  STAMP_FLIGHT_MS,
  STAMP_TOTAL_MS,
  type FlyingStamp,
  type StampAnchor,
  type StampPoint,
} from '~/lib/flying-stamps'
import { getRewardTreeAssets } from '~/lib/reward-tree-assets'
import type { DrawResult } from '~/lib/reward-types'
import { m } from '~/paraglide/messages'
import {
  adjustActivityTokenBalance,
  useActivityTokenBalance,
} from '~/resources/activity-token-balance'
import { FlyingIcons } from './flying-icons'
import { CurrencyPill } from './top-nav'
import { selectRewardHeading } from './reward-page-copy'

export function RewardPage(props: { draw: DrawResult; onClaim: () => void }) {
  const presentation = createMemo(() => deriveRewardPresentation(props.draw))
  return <RiveReward presentation={presentation()} onClaim={props.onClaim} />
}

// ---------------- Shared fly-out ----------------

function viewport() {
  return { width: window.innerWidth, height: window.innerHeight }
}

function anchorOf(el: Element | undefined): StampAnchor | null {
  if (!el) return null
  const r = el.getBoundingClientRect()
  return { left: r.left, top: r.top, width: r.width, height: r.height }
}

// ---------------- Rive flow ----------------

const SM = 'State Machine 1'
type RivePhase = 'pre-idle' | 'idle' | 'opening' | 'claimed'

async function loadDecodedImage(url: string) {
  const res = await fetch(url)
  const buf = await res.arrayBuffer()
  return decodeImage(new Uint8Array(buf))
}

function RiveReward(props: {
  presentation: RewardPresentation
  onClaim: () => void
}) {
  const treeName = useLocalizedTreeName(() => props.presentation.treeGid)
  const balanceQuery = useActivityTokenBalance()
  const queryClient = useQueryClient()
  const [displayedStamps, setDisplayedStamps] = createSignal(
    balanceQuery.data?.balance ?? 0,
  )
  const [isAnimatingStamps, setIsAnimatingStamps] = createSignal(false)
  // Guards handleClaim against double-invocation (rapid taps on the Claim
  // button, which has no native debounce). Without this, each extra tap fires
  // adjustActivityTokenBalance(+delta) again and the local balance over-credits
  // while the server only ever granted the stamps once.
  const [hasClaimed, setHasClaimed] = createSignal(false)
  createEffect(() => {
    if (isAnimatingStamps()) return
    const v = balanceQuery.data?.balance
    if (v !== undefined) setDisplayedStamps(v)
  })
  const heading = () =>
    selectRewardHeading({ isDuplicate: props.presentation.isDuplicate })()
  const subtitle = () => {
    const p = props.presentation
    if (p.isDuplicate) {
      return m.stamps_text({ number: String(p.stampQuantity) })
    }
    return treeName()
  }

  const [phase, setPhase] = createSignal<RivePhase>('pre-idle')
  const [flying, setFlying] = createSignal<FlyingStamp[]>([])
  let canvas: HTMLCanvasElement | undefined
  let stampPill: HTMLDivElement | undefined
  let rive: Rive | undefined
  let trgTap: ViewModelInstanceTrigger | null = null
  let onResize: (() => void) | undefined
  const refs: { unref(): void }[] = []
  const timers: number[] = []

  onMount(() => {
    if (!canvas) return
    const { full, blank } = getRewardTreeAssets(props.presentation.treeGid)

    rive = new Rive({
      canvas,
      src: rewardRiv,
      artboard: 'Cakebox',
      stateMachines: [SM],
      autoBind: true,
      autoplay: true,
      shouldDisableRiveListeners: true,
      layout: new Layout({ fit: Fit.Contain }),
      onLoad: async () => {
        if (!rive) return
        console.group('Rive dump')
        console.log('contents', rive.contents)
        const vm = rive.defaultViewModel()
        console.log('default VM', vm?.name, vm?.properties)
        console.log('bound instance props', rive.viewModelInstance?.properties)
        for (const sm of rive.contents?.artboards?.[0]?.stateMachines ?? []) {
          console.log(
            `SM "${sm.name}" inputs`,
            rive.stateMachineInputs(sm.name),
          )
        }
        console.groupEnd()

        console.log(rive?.contents)
        rive?.resizeDrawingSurfaceToCanvas()
        onResize = () => rive?.resizeDrawingSurfaceToCanvas()
        window.addEventListener('resize', onResize)

        const vmi = rive?.viewModelInstance
        if (!vmi) return

        const dup = vmi.boolean('isDuplicate')
        if (dup) dup.value = props.presentation.isDuplicate
        const num = vmi.number('numRewardType')
        if (num) num.value = props.presentation.numRewardType
        trgTap = vmi.trigger('trgTap')

        if (full) {
          const img = await loadDecodedImage(full)
          vmi.image('imgReward')!.value = img
          refs.push(img)
        }
        if (blank) {
          const img = await loadDecodedImage(blank)
          vmi.image('imgRewardBlank')!.value = img
          refs.push(img)
        }
      },
    })

    const handleStateChange = (e: { data?: unknown }) => {
      const states = (e.data as string[]) ?? []
      const kind = classifyRiveStates(states)
      if (kind === 'claimed') {
        setPhase('claimed')
      } else if (kind === 'idle' && phase() === 'pre-idle') {
        setPhase('idle')
      }
    }
    rive.on(EventType.StateChange, handleStateChange)

    onCleanup(() => {
      rive?.off(EventType.StateChange, handleStateChange)
      rive?.cleanup()
      if (onResize) window.removeEventListener('resize', onResize)
      for (const ref of refs) ref.unref()
      for (const t of timers) clearTimeout(t)
    })
  })

  const handleSurfaceTap = () => {
    if (phase() !== 'idle') return
    trgTap?.trigger()
    setPhase('opening')
  }

  const animateStampIncrement = (from: number, delta: number) =>
    animateCount({
      from,
      delta,
      onTick: setDisplayedStamps,
      onDone: () => setIsAnimatingStamps(false),
    })

  const pillCenter = (): StampPoint => {
    const vp = viewport()
    if (!stampPill) return { x: vp.width - 60, y: 60 }
    const r = stampPill.getBoundingClientRect()
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 }
  }

  // ~80% through STAMP_FLIGHT_MS is when each stamp visually meets the pill
  // (per the stampFly keyframes); pop and start counting a beat after that so
  // the number jumps in sync with the first impact.
  const STAMP_ARRIVAL_MS = Math.round(STAMP_FLIGHT_MS * 0.8)

  const triggerPillPop = () => {
    if (!stampPill) return
    stampPill.classList.remove('animate-stamp-pill-pop')
    void stampPill.offsetWidth
    stampPill.classList.add('animate-stamp-pill-pop')
  }

  const handleClaim = () => {
    if (hasClaimed()) return
    setHasClaimed(true)
    if (!props.presentation.isDuplicate) {
      props.onClaim()
      return
    }
    const delta = props.presentation.stampQuantity
    const from = displayedStamps()
    setIsAnimatingStamps(true)
    adjustActivityTokenBalance(queryClient, delta)
    const stamps = flyingStampsFrom(anchorOf(canvas), pillCenter(), viewport())
    setFlying(stamps)
    for (const stamp of stamps) {
      timers.push(
        window.setTimeout(triggerPillPop, stamp.delay + STAMP_ARRIVAL_MS),
      )
    }
    timers.push(
      window.setTimeout(
        () => animateStampIncrement(from, delta),
        STAMP_ARRIVAL_MS,
      ),
    )
    timers.push(window.setTimeout(props.onClaim, STAMP_TOTAL_MS))
  }

  return (
    <div
      class="fixed inset-0 z-[60] flex flex-col pt-[100px]"
      style={{
        background:
          'radial-gradient(106.67% 55.25% at 50.13% 44.75%, #FFE8CB 24.52%, #EFA4B6 55.77%, #926FB0 100%)',
      }}
      onClick={handleSurfaceTap}
    >
      <div
        class="pointer-events-none fixed inset-x-0 top-0 z-10 flex justify-end px-2"
        style={{ 'padding-top': 'max(var(--safe-area-top, 0px), 36px)' }}
      >
        <div ref={(el) => (stampPill = el)} class="pointer-events-auto py-2">
          <CurrencyPill icon={stampSvg} value={displayedStamps()} />
        </div>
      </div>

      <div class="flex flex-1 flex-col gap-6 px-6 pt-10 text-center">
        <Show when={phase() === 'claimed'}>
          <h1 class="text-xl font-semibold text-white">{heading()}</h1>
        </Show>

        <div class="relative h-[50vh]">
          <canvas
            ref={(el) => (canvas = el)}
            class="absolute left-1/2 top-0 h-full w-screen -translate-x-1/2"
            aria-hidden="true"
          />
        </div>

        <Show when={phase() === 'idle'}>
          <p class="text-center text-xl font-semibold text-white">
            {m.tap_to_open()}
          </p>
        </Show>

        <Show when={phase() === 'claimed'}>
          <p class="mt-6 text-[26px] font-bold text-white">{subtitle()}</p>
        </Show>
      </div>

      <Show when={phase() === 'claimed'}>
        <div class="flex flex-col px-6 pb-12 pt-4">
          <button
            type="button"
            class="mx-auto h-[52px] w-full max-w-[640px] rounded-xl bg-white px-4 font-bold text-[#176361] shadow-[0px_0px_20px_6px_rgba(223,221,255,0.5)] transition active:bg-[#e6e6e6] active:shadow-none disabled:opacity-70 disabled:shadow-none"
            disabled={hasClaimed()}
            onClick={(e) => {
              e.stopPropagation()
              handleClaim()
            }}
          >
            {m.cart_claim_active()}
          </button>
        </div>
      </Show>

      <FlyingIcons stamps={flying()} icon={stampSvg} />
    </div>
  )
}
